import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import {
  FolderIcon,
  UserIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  TagIcon,
  PlusIcon,
  XMarkIcon,
  GlobeAltIcon,
  CalendarIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Input, Select, Textarea, PageLoader } from '../../components/ui';
import { projectService, clientService } from '../../services';
import { useAuthStore } from '../../stores';
import dayjs from 'dayjs';

export default function ProjectForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuthStore();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      client: '',
      status: 'draft',
      priority: 'medium',
      startDate: '',
      endDate: '',
      budget: 0,
      cost: 0,
      currency: 'EGP',
      progress: 0,
      category: '',
      location: '',
      website: '',
      contactPerson: {
        name: '',
        email: '',
        phone: '',
        position: ''
      },
      tags: [],
      notes: '',
      customFields: [
        {
          key: 'projecttype',
          label: 'Project Type',
          type: 'text',
          value: 'Web Development',
          isExample: true
        }
      ]
    }
  });

  const { fields: customFields, append: appendCustomField, remove: removeCustomField } = useFieldArray({
    control,
    name: 'customFields'
  });

  const [tagInput, setTagInput] = useState('');
  const tags = watch('tags') || [];
  const selectedClient = watch('client');

  useEffect(() => {
    fetchClients();
    if (isEdit) {
      fetchProject();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Set default contact person for new projects when user is available
  useEffect(() => {
    if (!isEdit && user) {
      const currentContactPerson = watch('contactPerson');
      if (!currentContactPerson?.name && !currentContactPerson?.email) {
        reset({
          ...watch(),
          contactPerson: {
            name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || '',
            email: user.email || '',
            phone: user.phone || '',
            position: user.position || ''
          }
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isEdit]);

  const fetchClients = async () => {
    try {
      const response = await clientService.getClients({ limit: 100 });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const project = await projectService.getProject(id);
      reset({
        name: project.name || '',
        description: project.description || '',
        client: project.client?._id || '',
        isFreelancer: project.isFreelancer || false,
        freelancerName: project.freelancerName || '',
        freelancerEmail: project.freelancerEmail || '',
        freelancerPhone: project.freelancerPhone || '',
        status: project.status || 'draft',
        priority: project.priority || 'medium',
        startDate: project.startDate ? dayjs(project.startDate).format('YYYY-MM-DD') : '',
        endDate: project.endDate ? dayjs(project.endDate).format('YYYY-MM-DD') : '',
        budget: project.budget || 0,
        cost: project.cost || 0,
        currency: project.currency || 'EGP',
        progress: project.progress || 0,
        category: project.category || '',
        location: project.location || '',
        website: project.website || '',
        contactPerson: {
          name: project.contactPerson?.name || '',
          email: project.contactPerson?.email || '',
          phone: project.contactPerson?.phone || '',
          position: project.contactPerson?.position || ''
        },
        tags: project.tags || [],
        notes: project.notes || '',
        customFields: project.customFields || []
      });
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      const currentTags = watch('tags') || [];
      reset({ ...watch(), tags: [...currentTags, trimmedTag] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    const currentTags = watch('tags') || [];
    reset({ ...watch(), tags: currentTags.filter(tag => tag !== tagToRemove) });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const addCustomField = () => {
    appendCustomField({
      key: '',
      label: '',
      type: 'text',
      value: '',
      isExample: false
    });
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      // Clean the data - convert empty strings to null for optional fields
      const projectData = {
        name: (typeof data.name === 'string' ? data.name.trim() : data.name) || '',
        description: (data.description && typeof data.description === 'string') ? data.description.trim() || null : null,
        client: data.client || null,
        isFreelancer: data.isFreelancer || false,
        freelancerName: (data.freelancerName && typeof data.freelancerName === 'string') ? data.freelancerName.trim() || null : null,
        freelancerEmail: (data.freelancerEmail && typeof data.freelancerEmail === 'string') ? data.freelancerEmail.trim() || null : null,
        freelancerPhone: (data.freelancerPhone && typeof data.freelancerPhone === 'string') ? data.freelancerPhone.trim() || null : null,
        status: data.status || 'draft',
        priority: data.priority || 'medium',
        startDate: data.startDate || null,
        endDate: data.endDate || null,
        budget: data.budget ? parseFloat(data.budget) : 0,
        cost: data.cost ? parseFloat(data.cost) : 0,
        currency: data.currency || 'EGP',
        progress: Math.min(Math.max(data.progress || 0, 0), 100),
        category: (data.category && typeof data.category === 'string') ? data.category.trim() || null : null,
        location: (data.location && typeof data.location === 'string') ? data.location.trim() || null : null,
        website: (data.website && typeof data.website === 'string') ? data.website.trim() || null : null,
        contactPerson: {
          name: (data.contactPerson?.name && typeof data.contactPerson.name === 'string') ? data.contactPerson.name.trim() || null : null,
          email: (data.contactPerson?.email && typeof data.contactPerson.email === 'string') ? data.contactPerson.email.trim() || null : null,
          phone: (data.contactPerson?.phone && typeof data.contactPerson.phone === 'string') ? data.contactPerson.phone.trim() || null : null,
          position: (data.contactPerson?.position && typeof data.contactPerson.position === 'string') ? data.contactPerson.position.trim() || null : null
        },
        tags: data.tags?.filter(tag => tag && typeof tag === 'string' && tag.trim()) || [],
        notes: (data.notes && typeof data.notes === 'string') ? data.notes.trim() || null : null,
        customFields: data.customFields?.filter(field =>
          field && field.label && field.label.trim()
        ).map(field => {
          // Auto-generate key from label (lowercase, no spaces, alphanumeric only)
          const label = field.label.trim();
          const key = field.key?.trim() || label
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '')
            .replace(/[^a-z0-9]/g, '');

          return {
            key: key || 'field',
            label: label,
            type: field.type || 'text',
            value: field.value !== undefined && field.value !== '' ? field.value : null
          };
        }) || []
      };

      // Remove contactPerson if all fields are null
      if (Object.values(projectData.contactPerson).every(v => v === null)) {
        projectData.contactPerson = {};
      }

      // Remove isFreelancer field as it's no longer used
      delete projectData.isFreelancer;

      if (isEdit) {
        await projectService.updateProject(id, projectData);
        toast.success(t('projects.updated') || 'Project updated successfully');
        navigate('/projects');
      } else {
        const project = await projectService.createProject(projectData);
        toast.success(t('projects.created') || 'Project created successfully');
        navigate(`/projects/${project._id}`);
      }
    } catch (error) {
      console.error('Project update error:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.message || t('errors.serverError');
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = [
    { value: 'draft', label: t('projects.statuses.draft') || 'Draft' },
    { value: 'active', label: t('projects.statuses.active') || 'Active' },
    { value: 'on_hold', label: t('projects.statuses.onHold') || 'On Hold' },
    { value: 'completed', label: t('projects.statuses.completed') || 'Completed' },
    { value: 'cancelled', label: t('projects.statuses.cancelled') || 'Cancelled' }
  ];

  const priorityOptions = [
    { value: 'low', label: t('projects.priorities.low') || 'Low' },
    { value: 'medium', label: t('projects.priorities.medium') || 'Medium' },
    { value: 'high', label: t('projects.priorities.high') || 'High' },
    { value: 'urgent', label: t('projects.priorities.urgent') || 'Urgent' }
  ];

  const clientOptions = [
    { value: '', label: t('common.none') || 'None' },
    ...clients.map(c => ({ value: c._id, label: c.name }))
  ];

  const currencyOptions = [
    { value: 'EGP', label: 'EGP (E£)' },
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'SAR', label: 'SAR (﷼)' },
    { value: 'AED', label: 'AED (د.إ)' }
  ];

  const customFieldTypeOptions = [
    { value: 'text', label: t('common.text') || 'Text' },
    { value: 'number', label: t('common.number') || 'Number' },
    { value: 'date', label: t('common.date') || 'Date' },
    { value: 'boolean', label: t('common.boolean') || 'Boolean' },
    { value: 'select', label: t('common.select') || 'Select' },
    { value: 'textarea', label: t('common.textarea') || 'Textarea' }
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? t('projects.editProject') : t('projects.newProject')} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={isEdit ? t('projects.editProject') : t('projects.newProject')}
        backUrl="/projects"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <FolderIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('projects.basicInformation') || 'Basic Information'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('common.name')}
                required
                icon={FolderIcon}
                placeholder={t('projects.namePlaceholder') || 'Enter project name'}
                error={errors.name?.message}
                {...register('name', { required: t('errors.required') })}
              />

              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    label={t('common.status')}
                    options={statusOptions}
                    placeholder={t('common.select') || 'Select status'}
                    error={errors.status?.message}
                    {...field}
                  />
                )}
              />

              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select
                    label={t('common.priority')}
                    options={priorityOptions}
                    placeholder={t('common.select') || 'Select priority'}
                    error={errors.priority?.message}
                    {...field}
                  />
                )}
              />

              <Input
                label={t('projects.category') || 'Category'}
                placeholder={t('projects.categoryPlaceholder') || 'e.g., Web Development, Mobile App'}
                error={errors.category?.message}
                {...register('category')}
              />

              <div className="md:col-span-2">
                <Textarea
                  label={t('common.description')}
                  rows={4}
                  placeholder={t('projects.descriptionPlaceholder') || 'Enter project description'}
                  error={errors.description?.message}
                  {...register('description')}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Client Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <UserIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('projects.client') || 'Client'}
              </h2>
            </div>

            <Controller
              name="client"
              control={control}
              render={({ field }) => (
                <Select
                  label={t('projects.client')}
                  options={clientOptions}
                  placeholder={t('common.select') || 'Select client'}
                  error={errors.client?.message}
                  {...field}
                />
              )}
            />

            {(!selectedClient || selectedClient === '') && (
              <p className="mt-2 text-sm text-gray-500">
                {t('projects.noClientSelected') || 'This project has no clients'}
              </p>
            )}
          </CardBody>
        </Card>

        {/* Project Details Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <BuildingOfficeIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('projects.projectDetails') || 'Project Details'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('projects.location') || 'Location'}
                icon={MapPinIcon}
                placeholder={t('projects.locationPlaceholder') || 'Project location'}
                error={errors.location?.message}
                {...register('location')}
              />

              <Input
                label={t('projects.website') || 'Website'}
                type="url"
                icon={GlobeAltIcon}
                placeholder={t('projects.websitePlaceholder') || 'https://example.com'}
                error={errors.website?.message}
                {...register('website')}
              />

              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    {t('projects.contactPerson') || 'Contact Person'}
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={watch('contactPerson.name') === `${user?.firstName || ''} ${user?.lastName || ''}`.trim() &&
                        watch('contactPerson.email') === user?.email}
                      onChange={(e) => {
                        if (e.target.checked && user) {
                          const currentValues = watch();
                          reset({
                            ...currentValues,
                            contactPerson: {
                              name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || '',
                              email: user.email || '',
                              phone: user.phone || '',
                              position: user.position || ''
                            }
                          });
                        }
                      }}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">
                      {t('projects.useMeAsContact') || 'I am the contact person on this project'}
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label={t('common.name')}
                    placeholder={t('projects.contactPersonNamePlaceholder') || 'Contact person name'}
                    error={errors.contactPerson?.name?.message}
                    {...register('contactPerson.name')}
                  />

                  <Input
                    label={t('common.email')}
                    type="email"
                    placeholder={t('projects.contactPersonEmailPlaceholder') || 'contact@example.com'}
                    error={errors.contactPerson?.email?.message}
                    {...register('contactPerson.email', {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('errors.invalidEmail')
                      }
                    })}
                  />

                  <Input
                    label={t('common.phone')}
                    placeholder={t('projects.contactPersonPhonePlaceholder') || '+1234567890'}
                    error={errors.contactPerson?.phone?.message}
                    {...register('contactPerson.phone')}
                  />

                  <Input
                    label={t('projects.position') || 'Position'}
                    placeholder={t('projects.positionPlaceholder') || 'e.g., Project Manager'}
                    error={errors.contactPerson?.position?.message}
                    {...register('contactPerson.position')}
                  />
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Timeline & Budget Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <CalendarIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('projects.timelineAndBudget') || 'Timeline & Budget'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('projects.startDate')}
                type="date"
                // icon={CalendarIcon}
                error={errors.startDate?.message}
                {...register('startDate')}
              />

              <Input
                label={t('projects.endDate')}
                type="date"
                // icon={CalendarIcon}
                error={errors.endDate?.message}
                {...register('endDate')}
              />

              <Input
                label={t('projects.budget')}
                type="number"
                step="0.01"
                icon={CurrencyDollarIcon}
                placeholder="0.00"
                error={errors.budget?.message}
                {...register('budget', {
                  min: { value: 0, message: t('errors.minValue', { min: 0 }) }
                })}
              />

              <Input
                label={t('projects.cost') || 'Cost'}
                type="number"
                step="0.01"
                icon={CurrencyDollarIcon}
                placeholder="0.00"
                error={errors.cost?.message}
                {...register('cost', {
                  min: { value: 0, message: t('errors.minValue', { min: 0 }) }
                })}
              />

              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Select
                    label={t('projects.currency') || 'Currency'}
                    options={currencyOptions}
                    error={errors.currency?.message}
                    {...field}
                  />
                )}
              />

              <div>
                <label className="label mb-2">
                  {t('projects.progress') || 'Progress'} (%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  {...register('progress', {
                    min: { value: 0, message: t('errors.minValue', { min: 0 }) },
                    max: { value: 100, message: t('errors.maxValue', { max: 100 }) }
                  })}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span className="font-medium text-primary">{watch('progress') || 0}%</span>
                  <span>100%</span>
                </div>
                {errors.progress?.message && (
                  <p className="mt-1 text-sm text-danger">{errors.progress.message}</p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Tags Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <TagIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('common.tags')}
              </h2>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-danger"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={t('projects.addTagPlaceholder') || 'Add tag and press Enter'}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={addTag}
                className="flex-shrink-0"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                {t('common.add') || 'Add'}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Custom Fields Section */}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <TagIcon className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-neutral-dark">
                  {t('projects.customFields') || 'Custom Fields'}
                </h2>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustomField}
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                {t('common.add') || 'Add Field'}
              </Button>
            </div>

            {customFields.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                {t('projects.noCustomFields') || 'No custom fields added. Click "Add Field" to create one.'}
              </p>
            ) : (
              <div className="space-y-4">
                {customFields.map((field, index) => (
                  <div key={field.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                      <Input
                        label={t('projects.fieldLabel') || 'Label'}
                        placeholder={t('projects.fieldLabelPlaceholder') || 'Field label'}
                        error={errors.customFields?.[index]?.label?.message}
                        {...register(`customFields.${index}.label`, {
                          required: t('errors.required')
                        })}
                      />

                      <Controller
                        name={`customFields.${index}.type`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('projects.fieldType') || 'Type'}
                            options={customFieldTypeOptions}
                            error={errors.customFields?.[index]?.type?.message}
                            {...field}
                          />
                        )}
                      />

                      <div className="md:col-span-2">
                        {watch(`customFields.${index}.type`) === 'boolean' ? (
                          <Controller
                            name={`customFields.${index}.value`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                label={t('projects.fieldValue') || 'Value'}
                                options={[
                                  { value: 'true', label: t('common.yes') || 'Yes' },
                                  { value: 'false', label: t('common.no') || 'No' }
                                ]}
                                {...field}
                              />
                            )}
                          />
                        ) : watch(`customFields.${index}.type`) === 'date' ? (
                          <Input
                            label={t('projects.fieldValue') || 'Value'}
                            type="date"
                            {...register(`customFields.${index}.value`)}
                          />
                        ) : watch(`customFields.${index}.type`) === 'number' ? (
                          <Input
                            label={t('projects.fieldValue') || 'Value'}
                            type="number"
                            {...register(`customFields.${index}.value`)}
                          />
                        ) : watch(`customFields.${index}.type`) === 'textarea' ? (
                          <Textarea
                            label={t('projects.fieldValue') || 'Value'}
                            rows={3}
                            {...register(`customFields.${index}.value`)}
                          />
                        ) : (
                          <Input
                            label={t('projects.fieldValue') || 'Value'}
                            placeholder={t('projects.fieldValuePlaceholder') || 'Enter value'}
                            {...register(`customFields.${index}.value`)}
                          />
                        )}
                      </div>

                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeCustomField(index)}
                          className="text-danger hover:bg-danger/10 w-full"
                        >
                          <XMarkIcon className="w-4 h-4 mr-1" />
                          {t('common.remove') || 'Remove'}
                        </Button>
                      </div>
                    </div>
                    <input
                      type="hidden"
                      {...register(`customFields.${index}.key`)}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Notes Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <TagIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('common.notes')}
              </h2>
            </div>

            <Textarea
              rows={6}
              placeholder={t('projects.notesPlaceholder') || 'Enter project notes...'}
              error={errors.notes?.message}
              {...register('notes')}
            />
          </CardBody>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/projects')}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? t('common.update') : t('common.create')}
          </Button>
        </div>
      </form>
    </>
  );
}
